import {Check} from "../..";

const MILLIS_PER_SECOND = 1000;
const MILLIS_PER_MINUTE = MILLIS_PER_SECOND * 60;   //     60,000
const MILLIS_PER_HOUR = MILLIS_PER_MINUTE * 60;     //  3,600,000
const MILLIS_PER_DAY = MILLIS_PER_HOUR * 24;        // 86,400,000


export class TimeSpan {

    private _totalMilliseconds: number = 0;

    constructor(days: number, hours: number, minutes: number, seconds: number = 0, milliseconds: number = 0) {
        const totalmilliseconds = days * MILLIS_PER_DAY
            + hours * MILLIS_PER_HOUR
            + minutes * MILLIS_PER_MINUTE
            + seconds * MILLIS_PER_SECOND
            + Math.floor(milliseconds); // only milliseconds will be "trimmed"

        // try to avoid double number issue
        this._totalMilliseconds = Math.round(totalmilliseconds);
    }

    private _getSign() : number {
        if(this._totalMilliseconds < 0) {
            return -1;
        } else {
            return 1;
        }
    }

    private _floor(value: number): number {
        return this._getSign() * Math.floor(Math.abs(value));
    }

    get days(): number {
        return this._floor(this.totalDays);
    }

    get hours(): number {
        return this._floor(this.totalHours % 24);
    }

    get minutes(): number {
        return this._floor(this.totalMinutes % 60);
    }

    get seconds(): number {
        return this._floor(this.totalSeconds % 60);
    }

    get milliseconds(): number {
        return this._floor(this._totalMilliseconds % MILLIS_PER_SECOND);
    }

    get totalDays(): number {
        return this._totalMilliseconds / MILLIS_PER_DAY;
    }

    get totalHours(): number {
        return this._totalMilliseconds / MILLIS_PER_HOUR;
    }

    get totalMinutes(): number {
        return this._totalMilliseconds / MILLIS_PER_MINUTE;
    }

    get totalSeconds(): number {
        return this._totalMilliseconds / MILLIS_PER_SECOND;
    }

    get totalMilliseconds(): number {
        return this._totalMilliseconds;
    }

    static areEquals(ts1: TimeSpan, ts2: TimeSpan) {
        if(Check.isNullOrUndefined(ts1) && Check.isNullOrUndefined(ts2)){
            return true;
        }

        if(Check.isNullOrUndefined(ts1)) {
            return false;
        }

        if(Check.isNullOrUndefined(ts2)) {
            return false;
        }

        return ts1.totalMilliseconds === ts2.totalMilliseconds;
    }

    static parse(text: string | null | undefined) {
        if(!text) {
            throw new Error('Null, undefined or empty string not allowed for TimeSpan format');
        }

        const components = text.split(/:/);
        if(components.length < 2) {
            throw new Error('Invalid TimeSpan input format ' + text);
        }

        let days = 0;
        let hours = 0;
        let minutes = 0;
        let seconds = 0;
        let milliseconds = 0;

        const daysAndHours = components[0].split(/\./);
        if(daysAndHours.length === 2) {
            days = parseInt(daysAndHours[0]);
            hours = parseInt(daysAndHours[1]);
        } else {
            hours = parseInt(daysAndHours[0]);
        }

        minutes = parseInt(components[1]);

        if(components.length === 3) { // it means we have seconds and/or milliseconds
            let secondsAndMilliseconds = components[2].split(/\./);
            if(secondsAndMilliseconds.length === 2) { // it means we have seconds and milliseconds
                seconds = parseInt(secondsAndMilliseconds[0]);
                milliseconds = TimeSpan._parseMilliseconds(secondsAndMilliseconds[1]);
            } else { // it means we have seconds without milliseconds
                seconds = parseInt(secondsAndMilliseconds[0]);
            }
        }
        return new TimeSpan(days, hours, minutes, seconds, milliseconds);
    }

    static _parseMilliseconds(ms: string): number {
        let milliseconds = parseInt(ms);

        if(milliseconds <= 999) {
            return milliseconds;
        }

        while(milliseconds > 999) {
            milliseconds = milliseconds / 10;
        }

        return Math.round(milliseconds);
    }

    static fromMilliseconds(totalMilliseconds: number): TimeSpan {
        return new TimeSpan(0, 0, 0, 0, totalMilliseconds);
    }

    static fromSeconds(totalSeconds: number): TimeSpan {
        return new TimeSpan(0, 0, 0, totalSeconds, 0);
    }

    static fromMinutes(totalMinutes: number): TimeSpan {
        return new TimeSpan(0, 0, totalMinutes, 0, 0);
    }

    static fromHours(totalHours: number): TimeSpan {
        return new TimeSpan(0, totalHours, 0, 0, 0);
    }

    static fromDays(days: number): TimeSpan {
        return new TimeSpan(days, 0, 0, 0, 0);
    }

    subtract(timeSpan: TimeSpan): TimeSpan {
        if(!timeSpan) {
            throw new Error('timeSpan argument cannot be null or undefined');
        }

        const milliseconds = this.totalMilliseconds - timeSpan.totalMilliseconds;
        return TimeSpan.fromMilliseconds(milliseconds);
    }

    add(timeSpan: TimeSpan): TimeSpan {
        if(!timeSpan) {
            throw new Error('timeSpan argument cannot be null or undefined');
        }

        let totalMilliseconds = this.totalMilliseconds + timeSpan.totalMilliseconds;
        return TimeSpan.fromMilliseconds(totalMilliseconds);
    }

    public toString(options: {useTotalHours?: boolean} = {}): string {

        // special negative case (we treat it like '-'+normal positive toString() result)
        if (this._totalMilliseconds < 0)
            return '-' + TimeSpan.fromMilliseconds(-this._totalMilliseconds).toString({useTotalHours: options.useTotalHours});

        let result = '';
        if (!options.useTotalHours) {
            if(this.days > 0) {
                result += this.days.toString() + '.';
            }
            result += this.hours.toString().padStart(2, '0') + ':';
        } else {
            result += this._floor(this.totalHours).toString().padStart(2, '0') + ':';
        }
        
        result += this.minutes.toString().padStart(2, '0') + ':';
        result += this.seconds.toString().padStart(2, '0');

        if(this.milliseconds > 0) {
            result += '.' + this.milliseconds.toString().padStart(3, '0');
        }

        return result;
    }

    public toHoursAndMinutesString(): string {
        return this.hours.toString().padStart(2, '0') + ':' + this.minutes.toString().padStart(2, '0');
    }

    public toHoursMinutesAndSecondsString(): string {
        return this.hours.toString().padStart(2, '0') + ':' + this.minutes.toString().padStart(2, '0') + ':' + this.seconds.toString().padStart(2, '0');
    }

    public toLocalTime(): TimeSpan {
        let localTime = new Date();
        localTime.setUTCHours(this.hours);
        localTime.setUTCMinutes(this.minutes);
        localTime.setUTCSeconds(this.seconds);
        localTime.setUTCMilliseconds(this.milliseconds);

        return new TimeSpan(0, localTime.getHours(), localTime.getMinutes(), localTime.getSeconds(), localTime.getMilliseconds());
    }
}
